import React, {
    memo,
    Fragment,
    useCallback,
    useEffect,
    useMemo, useState,
} from "react";

import Title from "../common/title";
import FixtureEditorAngle from "../fixtureEditorAngle/fixtureEditorAngle";
import Dropdown from "../common/dropdown";
import { usePlayer } from "../../store/player/usePlayer";
import * as constants from "../../constants/fixture.constants";
import { FixtureFiles as FixtureFilesType } from "../../utils/hooks/useFixtureFootage";
import { IncidentFiles as IncidentFilesType } from "../../utils/hooks/useFixtureIncidents";
import styles from "./fixtureEditorAngles.module.scss";
import useEditorAngles from "../../store/editorAngles/useEditorAngles";
import {Angle} from "../../store/editorAngles/types";
import {filesService} from "../../services/files.service";
import {fetchAudioTracksFromManifest} from "../../utils/audioTracks";

export interface FixtureEditorAnglesProps {
    files: FixtureFilesType[] | undefined;
}

export interface TrackInfo {
    audio: {
        id: number;
        name?: string;
        language?: string | null;
        default: boolean;
        forced: boolean;
    }[];
}

export interface FileTracks {
    fileId: string;
    url: string;
    tracks: TrackInfo | null;
    loading: boolean;
    error: any;
}

const FixtureEditorAngles = memo<FixtureEditorAnglesProps>(({ files = []  }) => {
    const { source } = usePlayer();
    const { setDropdownAngles, setSelectedAngles, anglesInitialised, dropdownAngles, selectedAngles, anglesAudio, setAnglesInitialised, setAnglesAudio } = useEditorAngles();
    const [allAvailableOptions, setAllAvailableOptions] = useState<{ angleId: string; angleName: string, audioTrackId: number | null; audioName: string; }[]>([]);
    const [fileTracks, setFileTracks] = useState<FileTracks[]>([]);


    const getTracksForFiles = useCallback(async () => {
        let isMounted = true;

        for (const file of files) {
            try {
                const response = await filesService.getSignedLink(file.id);
                const url = response?.location;
                if (!url) throw new Error("No signed URL returned");

                const audioTracks = await fetchAudioTracksFromManifest(url);

                if (isMounted) {
                    const newFileTrack: FileTracks = {
                        fileId: file.id,
                        url,
                        tracks: {
                            audio: audioTracks.map(({ id, name, language, default: d, forced }) => ({
                                id, name, language, default: d, forced,
                            })),
                        },
                        loading: false,
                        error: null,
                    };

                    setFileTracks((prev) => {
                        const updated = prev.filter((t) => t.fileId !== file.id);
                        return [...updated, newFileTrack];
                    });
                }
            } catch (error) {
                if (isMounted) {
                    const failedTrack: FileTracks = {
                        fileId: file.id,
                        url: "",
                        tracks: null,
                        loading: false,
                        error,
                    };

                    setFileTracks((prev) => {
                        const updated = prev.filter((t) => t.fileId !== file.id);
                        return [...updated, failedTrack];
                    });
                }
            }
        }

        return () => {
            isMounted = false;
        };
    }, [files]);




    useEffect(() => {
        if (!files?.length) return;

        setFileTracks(
            files.map((file) => ({
                fileId: file.id,
                url: "",
                tracks: null,
                loading: true,
                error: null,
            }))
        );

        (async () => {
            try {
                await getTracksForFiles();
            } catch (err) {
                console.error("Error fetching tracks:", err);
            }
        })();
    }, [files, getTracksForFiles]);


    const setFileLabel = useCallback((file: FixtureFilesType | IncidentFilesType) => {
        if (file.sub_type === "VIDEO_CLIP" && "incident" in file.metadata) {
            return file.metadata.incident || "";
        }
        return constants.VIDEO_TYPES.find((type) => type.id === file.sub_type)?.name || "";
    }, []);

    const getFileFromLabel = useCallback((label: string): FixtureFilesType | undefined => {
        return files.find((file) => setFileLabel(file) === label);
    }, [files, setFileLabel]);

    const videoFiles = useMemo(() => {
        return files.filter((file) => file.file_type === constants.VIDEO && file.sub_type !== "VIDEO_HIGHLIGHTS");
    }, [files]);

    const isAudioAvailable = useMemo(() => {
        return files.find((file) => file.sub_type === "AUDIO_COMMS");
    }, [files]);

    const renderDropdown = useMemo(() => {
        return selectedAngles.length < allAvailableOptions.length;
    }, [selectedAngles, allAvailableOptions]);

    const dropdownAngleNames = useMemo(() => {
        return dropdownAngles.map((file) => setFileLabel(file));
    },[dropdownAngles, setFileLabel]);

    const getAudioOptions = useCallback((fileId: string) => {
        const audioTracks = fileTracks.find((f) => f.fileId.toString() === fileId)?.tracks?.audio;
        return isAudioAvailable && audioTracks ? audioTracks : [];
    }, [fileTracks, isAudioAvailable]);

    const defineDropdownAngles = useCallback(() => {
        const newDropdown = []

        for (const file of videoFiles){
            const allAngleOptions = allAvailableOptions.filter((option) => option.angleId.toString() === file.id.toString())
            const selectedAngleOptions = selectedAngles.filter((selected) => selected.angleId.toString() === file.id.toString());
            if (selectedAngleOptions.length < allAngleOptions.length) {
                newDropdown.push(file);
            }
        }

        const isSame =
            newDropdown.length === dropdownAngles.length &&
            newDropdown.every((file, i) => file.id === dropdownAngles[i]?.id);

        if (!isSame) {
            setDropdownAngles(newDropdown);
        }
    }, [videoFiles, selectedAngles, dropdownAngles, setDropdownAngles, allAvailableOptions]);

    const mapFixtureFilesToAngle = useCallback((file: FixtureFilesType): Angle => {
        const existingAngles = selectedAngles.filter((angle) => angle.angleId.toString() === file.id.toString());
        const maxElementId = existingAngles.reduce((max, angle) => Math.max(max, parseInt(angle.elementId)), 0);
        var elementId = 0;
        if (existingAngles.length !== 0) {
            elementId = maxElementId + 1;
        }

        const name = setFileLabel(file);
        const key = `angle-${name}-${elementId}`;
        return {
            elementId: elementId.toString(),
            angleId: file.id.toString(),
            angleName: setFileLabel(file),
            elementKey: key,
        };
    }, [selectedAngles, setFileLabel]);



    const addSelectedAngle = useCallback((e: React.FormEvent<EventTarget>) => {
        const target = e.target as HTMLElement;
        const angle = getFileFromLabel(target.id);
        if (!angle) return;
        const allAngleOptions = allAvailableOptions.filter((option) => option.angleId.toString() === angle.id.toString())
        const selectedAngleOptions = selectedAngles.filter((selected) => selected.angleId.toString() === angle.id.toString());

        if (selectedAngleOptions.length < allAngleOptions.length) {
            const updated = [...selectedAngles, mapFixtureFilesToAngle(angle)]
            setSelectedAngles(updated);
        }
    }, [selectedAngles, getFileFromLabel, setSelectedAngles, mapFixtureFilesToAngle, allAvailableOptions]);

    const removeSelectedAudio = useCallback((angleId: string) => {
        const updatedAudio = anglesAudio.filter((audio) => audio.angleId.toString() !== angleId);
        setAnglesAudio(updatedAudio);
    }, [anglesAudio, setAnglesAudio]);

    const removeSelectedAngle = useCallback((elementKey: string) => {
        const updated = selectedAngles.filter((file) => file.elementKey.toString() !== elementKey);
        removeSelectedAudio(elementKey);
        setSelectedAngles(updated);
    }, [selectedAngles, setSelectedAngles, removeSelectedAudio]);

    useEffect(() => {
        if (!anglesInitialised && videoFiles.length > 0 && source?.id) {
            const sourceFile = videoFiles.find((file) => file.id.toString() === source.id);
            if (sourceFile && !selectedAngles.some((f) => f.angleName === sourceFile.id)) {
                setSelectedAngles([mapFixtureFilesToAngle(sourceFile), ...selectedAngles]);
            }
            setAnglesInitialised(true);
        }
    },[anglesInitialised, videoFiles, source?.id, setSelectedAngles, selectedAngles, setAnglesInitialised, mapFixtureFilesToAngle]);

    useEffect(() => {
        if (videoFiles.length > 0) {
            const options = []
            for (const file of videoFiles) {
                if (isAudioAvailable){
                    const fileAudio = fileTracks.find((f) => f.fileId.toString() === file.id.toString())?.tracks?.audio;
                    if (!fileAudio) {
                        options.push({
                            angleId: file.id.toString(),
                            angleName: setFileLabel(file),
                            audioTrackId: null,
                            audioName: "",
                        });
                        continue;
                    }
                    for (const audio of fileAudio) {
                        const option = {
                            angleId: file.id.toString(),
                            angleName: setFileLabel(file),
                            audioTrackId: audio.id,
                            audioName: audio?.name || "",
                        }

                        options.push(option);
                    }
                }else{
                    options.push({
                        angleId: file.id.toString(),
                        angleName: setFileLabel(file),
                        audioTrackId: 0,
                        audioName: "",
                    });
                }
            }
            if (JSON.stringify(options) !== JSON.stringify(allAvailableOptions)) {
                setAllAvailableOptions(options);
            }
        }
    }, [videoFiles, fileTracks, setAllAvailableOptions, setFileLabel, isAudioAvailable, allAvailableOptions]);

    useEffect(() => {
        defineDropdownAngles();
    }, [defineDropdownAngles]);

    return (
        <div className={styles.fixtureEditorAngles}>
            <Title label="Angles" classes={`subtitle ${styles.title}`} />
            {selectedAngles.length > 0 &&(
                <Fragment>
                    {selectedAngles.map((angle) => (
                        <FixtureEditorAngle
                            key={angle.elementKey}
                            angleId={angle.angleId.toString()}
                            elementKey={angle.elementKey}
                            label={angle.angleName}
                            onClickOption={removeSelectedAngle}
                            audioOptions={getAudioOptions(angle.angleId.toString())}
                        />
                    ))}
                </Fragment>
            )}
            {renderDropdown && (
                <div className={styles.dropdown}>
                    <Dropdown
                        id="manageAngles"
                        icon={{
                            icon: {
                                icon: ["fas", "plus"],
                                type: "FontAwesomeIcon",
                                size: "1x"
                            }
                        }}
                        classes={`btn btn-circle-sm icon btn-white ${styles.button}`}
                        onClick={addSelectedAngle}
                        onOpen={() => {}}
                        onClose={() => {}}
                        tooltip="Manage Angles"
                        suggestions={dropdownAngleNames}
                    />
                </div>
            )}
        </div>
    );
});

export default FixtureEditorAngles;
